import React, { useEffect, useMemo, useState, useCallback } from "react";
import ForwardCurveGraph from "./forwardCurveGraph"; // your template component
import Loading from "../../../common/components/loading/loading";

const ForwardCurves = ({ settlements, isLoading }) => {
  // Parse UTC dates in a consistent way
  const parseUtcDate = (dateStr) => {
    const clean = dateStr
      .replace("T00:00:00.000Z", "")
      .replace("T00:00:00Z", "")
      .replace("T00:00:00", "");
    const [y, m, d] = clean.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };

  // All available market dates, sorted ascending
  const availableDates = useMemo(() => {
    if (!settlements?.length) return [];
    return Array.from(
      new Set(settlements.map((x) => parseUtcDate(x.marketDate).getTime()))
    )
      .sort((a, b) => a - b)
      .map((t) => new Date(t));
  }, [settlements]);

  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (availableDates.length) {
      setSelectedDate(availableDates[availableDates.length - 1]); // latest date
    }
  }, [availableDates]);

  const buildForwardCurve = useCallback(
    (prefix) => {
      if (!selectedDate) return { settlements: [], usedDate: null };

      // Filter dates where this prefix has data
      const validDates = settlements
        .filter(
          (x) => (x[`${prefix}_settlePrice`] ?? x[`${prefix}_current`]) != null
        )
        .map((x) => parseUtcDate(x.marketDate));

      if (!validDates.length) return { settlements: [], usedDate: null };

      // Find the most recent date <= selectedDate
      const closestDate = validDates
        .filter((d) => d.getTime() <= selectedDate.getTime())
        .sort((a, b) => b - a)[0];

      if (!closestDate) return { settlements: [], usedDate: null };

      const data = settlements
        .filter(
          (x) => parseUtcDate(x.marketDate).getTime() === closestDate.getTime()
        )
        .map((x) => ({
          marketDate: x.marketDate,
          termName: x[`${prefix}_termName`] ?? "N/A",
          settlePrice:
            x[`${prefix}_settlePrice`] ?? x[`${prefix}_current`] ?? null,
          beginDate: x.beginDate,
          endDate: x.endDate,
        }))
        .filter((x) => x.settlePrice !== null)
        .filter((x) => x.termName !== null && !x.termName.startsWith("Cal")); // Filter out Cal terms

      return { settlements: data, usedDate: closestDate };
    },
    [selectedDate, settlements] // <-- only change when selectedDate or settlements change
  );

  const ngxPower = useMemo(
    () => buildForwardCurve("NGXPower"),
    [buildForwardCurve]
  );
  const ngxGas = useMemo(
    () => buildForwardCurve("NGXGas"),
    [buildForwardCurve]
  );
  const nymexGas = useMemo(
    () => buildForwardCurve("NYMEXGas"),
    [buildForwardCurve]
  );
  const nymexCrude = useMemo(
    () => buildForwardCurve("NYMEXCrude"),
    [buildForwardCurve]
  );

  const [hoverDate, setHoverDate] = useState(null);
  const [hoverPos, setHoverPos] = useState(0);

  if (isLoading) return <Loading />;

  const formatDate = (date) => (date ? date.toISOString().slice(0, 10) : "N/A");

  return (
    <div className="container-fluid">
      {/* Slider */}
      {availableDates.length > 0 && (
        <div style={{ margin: "10px 0" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>
            Select Date for Graphs: {formatDate(selectedDate)}
          </label>
          <input
            type="range"
            min={0}
            max={availableDates.length - 1}
            value={
              selectedDate && availableDates.length
                ? availableDates.findIndex(
                    (d) => d.getTime() === selectedDate.getTime()
                  )
                : 0
            }
            onChange={(e) => {
              const idx = Number(e.target.value);
              setSelectedDate(availableDates[idx]);
            }}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const percent = (e.clientX - rect.left) / rect.width;
              const idx = Math.round(percent * (availableDates.length - 1));
              const date = availableDates[idx];
              setHoverDate(date);
              setHoverPos(percent * 100);
            }}
            onMouseLeave={() => setHoverDate(null)}
            style={{
              width: "100%",
              height: "8px",
              borderRadius: "4px",
              background: " #adb5bd", // track color
              appearance: "none",
              outline: "none",
              padding: 0,
              margin: 0,
              WebkitAppearance: "none",
              MozAppearance: "none",
              /* Thumb styles for Webkit */
              WebkitSliderThumb: {
                appearance: "none",
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: "var(--theme-interactive-element)",
                cursor: "pointer",
                marginTop: "-4px", // center thumb on track
              },
              /* Thumb styles for Firefox */
              MozRangeThumb: {
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: "var(--theme-interactive-element)",
                cursor: "pointer",
              },
              /* Thumb styles for IE/Edge */
              msThumb: {
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                background: "var(--theme-interactive-element)",
                cursor: "pointer",
              },
            }}
          />
          {hoverDate && (
            <div
              style={{
                position: "absolute",
                top: "120px",
                left: `${hoverPos}%`,
                transform: "translateX(-50%)",
                background: "#333",
                color: "#fff",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "12px",
                pointerEvents: "none",
                whiteSpace: "nowrap",
              }}
            >
              {formatDate(hoverDate)}
            </div>
          )}
        </div>
      )}

      {/* Graphs */}
      <div className="row" style={{ height: "400px" }}>
        <div className="col-md-6 tile" style={{ height: "100%" }}>
          <ForwardCurveGraph
            data={nymexGas?.settlements}
            title={`NYMEX Gas Forward Curve (${formatDate(
              nymexGas?.usedDate
            )})`}
            yAxisLabel="USD/MMBTU"
            isLoading={isLoading}
          />
        </div>
        <div className="col-md-6 tile" style={{ height: "100%" }}>
          <ForwardCurveGraph
            data={ngxGas?.settlements}
            title={`NGX Gas Forward Curve (${formatDate(ngxGas?.usedDate)})`}
            yAxisLabel="CAD/GJ"
            isLoading={isLoading}
          />
        </div>
      </div>

      <div className="row" style={{ height: "400px" }}>
        <div className="col-md-6 tile" style={{ height: "100%" }}>
          <ForwardCurveGraph
            data={nymexCrude?.settlements}
            title={`NYMEX Crude Forward Curve (${formatDate(
              nymexCrude?.usedDate
            )})`}
            yAxisLabel="USD/BBL"
            isLoading={isLoading}
          />
        </div>
        <div className="col-md-6 tile" style={{ height: "100%" }}>
          <ForwardCurveGraph
            data={ngxPower?.settlements}
            title={`NGX Power Forward Curve (${formatDate(
              ngxPower?.usedDate
            )})`}
            yAxisLabel="CAD/MWh"
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ForwardCurves;
